import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Spinner } from './Spinner';
import './Button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and disables interaction while preserving the button width. */
  loading?: boolean;
  /** Icon rendered before the label. Pass a 16px lucide icon. */
  leadingIcon?: ReactNode;
  /** Icon rendered after the label. */
  trailingIcon?: ReactNode;
  /** Stretch to the width of the container. */
  fullWidth?: boolean;
}

/**
 * As classes de um botão desta variante e tamanho. Exportada para que um link de
 * navegação (`<Link>`) com cara de botão vista o mesmo vocabulário, em vez de
 * uma imitação copiada à mão que se afasta do primitivo com o tempo.
 */
export function buttonClassName(
  variant: ButtonVariant = 'secondary',
  size: ButtonSize = 'md',
  fullWidth = false,
): string {
  return cn('btn', `btn--${variant}`, `btn--${size}`, fullWidth && 'btn--full');
}

/**
 * Primary action control. One shape, four intents, two sizes; every state from
 * the design system is covered in CSS. While `loading`, the label stays in flow
 * (hidden via opacity) so the width never jumps, and a spinner overlays it.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    leadingIcon,
    trailingIcon,
    fullWidth = false,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonClassName(variant, size, fullWidth), loading && 'btn--loading', className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && (
        <span className="btn__spinner" aria-hidden="true">
          <Spinner size={size === 'sm' ? 14 : 16} />
        </span>
      )}
      <span className="btn__content">
        {leadingIcon && (
          <span className="btn__icon" aria-hidden="true">
            {leadingIcon}
          </span>
        )}
        {children != null && <span className="btn__label">{children}</span>}
        {trailingIcon && (
          <span className="btn__icon" aria-hidden="true">
            {trailingIcon}
          </span>
        )}
      </span>
    </button>
  );
});
